import * as React from 'react';
import { FC } from 'react';
import { Form } from 'reactstrap';
import { useTranslations } from '../../../store/translations/hooks';
import { TK } from '../../../store/translations/translationKeys';
import { AutocompleteTextField, TextInputField, TimeField, SwitchField } from '../../../components/Forms/fields';
import { Row } from '../../../components/Forms/table/Row';
import { Col } from '../../../components/Forms/table/Column';
import { ServiceSettings, ServiceType } from '../../../models/api-models';
import { minutesToTimeString, parseTimeStringToMinutes } from '../../../shared/utils';
import ColorBall from '../../../components/ColorBall';

interface ServiceConfigurationSettingsTabProps {
    id: string;
    data: ServiceSettings;
    rooms: string[];
    categories: string[];
    nameAlreadyExists: boolean;
    setData: React.Dispatch<React.SetStateAction<ServiceSettings>>;
}

const ServiceConfigurationSettingsTab: FC<ServiceConfigurationSettingsTabProps> = ({
    id,
    data,
    rooms,
    categories,
    nameAlreadyExists,
    setData,
}) => {
    const t = useTranslations();

    return (
        <Form style={{ display: 'flex', marginTop: 'var(--spacer-S)', flexDirection: 'column', gap: 'var(--spacer-M)' }}>
            <Row>
                <Col md={6}>
                    <TextInputField
                        id={`${id}-name`}
                        label={t(TK.name)}
                        helpText={t(TK.serviceNameNotes)}
                        value={data.name}
                        invalid={nameAlreadyExists}
                        description={nameAlreadyExists ? t(TK.serviceNameAlreadyExists) : undefined}
                        onChange={name => setData(d => ({ ...d, name }))}
                    />
                </Col>
                <Col md={6}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacer-S)' }}>
                        <AutocompleteTextField
                            id={`${id}-category`}
                            style={{ flexGrow: 1 }}
                            label={t(TK.category)}
                            helpText={t(TK.serviceCategoryNotes)}
                            options={categories}
                            value={data.category || ''}
                            onValueChanged={category => setData(d => ({ ...d, category }))}
                            onAddNewOption={category => setData(d => ({ ...d, category }))}
                        />
                        <ColorBall
                            color={data.color}
                            onChange={color => setData(d => ({ ...d, color }))}
                        />
                    </div>
                </Col>
            </Row>
            <Row>
                <Col md={6}>
                    <TimeField
                        id={`${id}-duration`}
                        label={t(TK.duration)}
                        helpText={t(TK.serviceDurationNotes)}
                        value={minutesToTimeString(data.defaultDurationMinutes ?? 60)}
                        onChange={time =>
                            setData(d => ({
                                ...d,
                                defaultDurationMinutes: parseTimeStringToMinutes(time),
                            }))
                        }
                    />
                </Col>
                <Col md={6}>
                    <SwitchField
                        id={`${id}-group-appointment`}
                        label={t(TK.groupAppointment)}
                        helpText={t(TK.serviceGroupNotes)}
                        value={data.serviceType === ServiceType.GroupAppointment}
                        onChange={value => setData(d => ({ ...d, serviceType: value ? ServiceType.GroupAppointment : ServiceType.ClientAppointment }))}
                    />
                </Col>
            </Row>
            <Row>
                <Col md={12}>
                    <TextInputField
                        type="text"
                        id={`${id}-description`}
                        label={t(TK.description)}
                        helpText={t(TK.serviceDescriptionNotes)}
                        value={data.description}
                        onChange={description => setData(d => ({ ...d, description }))}
                    />
                </Col>
            </Row>
            <Row>
                <Col md={12}>
                    <AutocompleteTextField
                        id={`${id}-rooms`}
                        label={t(TK.rooms)}
                        helpText={t(TK.serviceRoomsNotes)}
                        options={rooms}
                        values={data.defaultRooms ?? []}
                        onValuesChanged={defaultRooms => setData(d => ({ ...d, defaultRooms }))}
                        multiSelect
                    />
                </Col>
            </Row>
        </Form>
    );
};

export default ServiceConfigurationSettingsTab;
